import { z } from 'zod'
import { ConfigDescription } from '../../../helpers/widget'
import { JsonValue } from './bridge/bridge-protocol'

/**
 * html-config.ts
 *
 * Lets an embedded page declare extra widget-config fields ("config from HTML").
 * The host renders one form field per declaration in the widget-config dialog,
 * alongside the fixed fields. The page declares them in a JSON <script> block:
 *
 *   <script type="application/json" id="wistor-config-fields">
 *   [ {"name":"heading","type":"text","label":"Panel heading","defaultValue":"Notes"},
 *     {"name":"readOnly","type":"boolean","label":"Read-only"},
 *     {"name":"columns","type":"number","defaultValue":2},
 *     {"name":"theme","type":"select","options":[{"label":"Light","value":"light"},
 *                                                {"label":"Dark","value":"dark"}]} ]
 *   </script>
 *
 * Values are stored in the flat widget config under a "configField:" prefix (so
 * they can never collide with the fixed query/html/config/targetGraph keys) and
 * surfaced to the page as window.wistor.configFields (bare names). Only simple
 * scalar field types are allowed — structured config still belongs in `config`.
 */

export const CONFIG_FIELD_PREFIX = 'configField:'
const SCRIPT_ID = 'wistor-config-fields'
const DEFAULT_GROUP = 'Config from HTML'

const FIELD_TYPES = ['text', 'textarea', 'number', 'boolean', 'select'] as const

const optionSchema = z.object({ label: z.string(), value: z.string() })

const htmlConfigFieldSchema = z.object({
  name: z.string().regex(/^[A-Za-z_][\w-]*$/),
  type: z.enum(FIELD_TYPES),
  label: z.string().optional(),
  options: z.array(optionSchema).optional(),
  defaultValue: z.union([z.string(), z.number(), z.boolean()]).optional(),
  formGroup: z.string().optional(),
})

export type HtmlConfigField = z.infer<typeof htmlConfigFieldSchema>

/**
 * Extract and validate the config-field declarations from a page's HTML.
 * Fail-safe: any missing/malformed block yields an empty list rather than
 * throwing, so a half-written page never breaks the config dialog.
 */
export function parseHtmlConfigFields(html: string | null | undefined): HtmlConfigField[] {
  if (!html) return []
  const match = html.match(
    new RegExp(`<script[^>]*id=["']${SCRIPT_ID}["'][^>]*>([\\s\\S]*?)<\\/script>`, 'i')
  )
  if (!match) return []

  const parsed = (() => {
    try {
      return z.array(htmlConfigFieldSchema).safeParse(JSON.parse(match[1]))
    } catch {
      return null
    }
  })()
  if (!parsed || !parsed.success) return []

  // First declaration of a name wins; later duplicates are dropped.
  const seen = new Set<string>()
  return parsed.data.filter((f) => (seen.has(f.name) ? false : (seen.add(f.name), true)))
}

/** Map declared fields to prefixed, grouped config descriptions for FormFields. */
export function toConfigDescriptions(fields: HtmlConfigField[]): ConfigDescription<any, any, any>[] {
  return fields.map((f) => ({
    name: CONFIG_FIELD_PREFIX + f.name,
    type: f.type,
    label: f.label ?? f.name,
    formGroup: f.formGroup ?? DEFAULT_GROUP,
    ...(f.options ? { options: f.options } : {}),
    ...(f.defaultValue !== undefined ? { defaultValue: String(f.defaultValue) } : {}),
  })) as ConfigDescription<any, any, any>[]
}

/**
 * Read the stored value for each declared field and coerce it to a sensible JS
 * type for the page (boolean/number/string). Invalid or missing values — e.g.
 * after a field was re-typed or a select option was removed — fall back to the
 * declared default, so runtime is always clean regardless of stale config.
 *
 * `resolve`, when provided, is applied to string values before coercion so a
 * builder can put {{appVar}} placeholders in a field (interpolated before a
 * number/boolean field is coerced too, e.g. "{{count}}" -> "5" -> 5).
 */
export function readConfigFieldValues(
  fields: HtmlConfigField[],
  rawConfig: Record<string, unknown>,
  resolve?: (value: string) => string
): Record<string, JsonValue> {
  const out: Record<string, JsonValue> = {}
  for (const f of fields) {
    const stored = rawConfig[CONFIG_FIELD_PREFIX + f.name]
    const raw = typeof stored === 'string' && resolve ? resolve(stored) : stored
    out[f.name] = coerce(f, raw)
  }
  return out
}

function coerce(field: HtmlConfigField, raw: unknown): JsonValue {
  const fallback = field.defaultValue
  switch (field.type) {
    case 'boolean':
      if (typeof raw === 'boolean') return raw
      if (raw === 'true') return true
      if (raw === 'false') return false
      return typeof fallback === 'boolean' ? fallback : false
    case 'number': {
      const n = typeof raw === 'number' ? raw : raw != null && raw !== '' ? Number(raw) : NaN
      if (!Number.isNaN(n)) return n
      return typeof fallback === 'number' ? fallback : fallback != null ? Number(fallback) : null
    }
    case 'select': {
      const allowed = new Set((field.options ?? []).map((o) => o.value))
      if (typeof raw === 'string' && allowed.has(raw)) return raw
      const def = fallback != null ? String(fallback) : ''
      return allowed.has(def) ? def : field.options?.[0]?.value ?? null
    }
    default: // text | textarea
      if (typeof raw === 'string' && raw !== '') return raw
      return fallback != null ? String(fallback) : ''
  }
}
